import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { emailSchema, passwordSchema } from '@tailoring-crm/shared';
import { AuthLayout } from '../components/auth/auth-layout.js';
import { FormError, PasswordField, EmailField } from '../components/auth/auth-fields.js';
import { OtpStep } from '../components/auth/otp-step.js';
import { Button, Input, Label } from '@tailoring-crm/ui';
import { useApiClient } from '../lib/api/api-context.js';
import { completeSignup, startSignup, verifySignup } from '../lib/auth/auth-api.js';
import type { OtpChallenge } from '../lib/auth/auth-contract.js';
import { useAuth } from '../lib/auth/auth-context.js';
import { authErrorMessage } from '../lib/auth/auth-errors.js';
import { rememberDevice } from '../lib/auth/remembered-device.js';

/**
 * Signing up: an address, the code that proves it, and the two names that make a shop.
 *
 * Three steps rather than one long form, because the middle one is a hard gate — the address
 * has to answer an email before anything else is worth typing — and because a person on a phone
 * abandons a form they cannot see the end of. The step counter is always visible for the same
 * reason.
 *
 * "No card needed" is on the first screen and not the last. It is the single most common
 * reason a small shop closes a signup page, and answering it after they have typed their
 * email address is answering it too late.
 *
 * The landing site's hero (apps/landing, on its own domain) asks for the address itself and
 * links here as `?email=`; a valid one skips the first step and the code is sent straight away.
 */
type Step = 'email' | 'code' | 'details';

export function SignupPage(): ReactElement {
  const { t } = useTranslation();
  const client = useApiClient();
  const auth = useAuth();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null);
  const [verificationToken, setVerificationToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | undefined>(undefined);

  const [ownerName, setOwnerName] = useState('');
  const [shopName, setShopName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | undefined>(undefined);

  const [searchParams] = useSearchParams();
  const handedOver = useRef(false);

  const requestCode = async (address: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      setChallenge(await startSignup(client, address));
      setStep('code');
    } catch (caught) {
      setError(authErrorMessage(caught, t));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    // Once only: StrictMode runs effects twice, and a second code would void the first.
    if (handedOver.current) return;
    handedOver.current = true;
    const address = searchParams.get('email');
    if (!address) return;
    const parsed = emailSchema.safeParse(address);
    if (!parsed.success) {
      setEmail(address);
      return;
    }
    setEmail(parsed.data);
    void requestCode(parsed.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the address is read on arrival only
  }, []);

  const submitEmail = (): void => {
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setEmailError(parsed.error.issues[0]?.message);
      return;
    }
    setEmailError(undefined);
    setEmail(parsed.data);
    void requestCode(parsed.data);
  };

  const submitCode = (code: string): void => {
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        const verified = await verifySignup(client, { email, code });
        setVerificationToken(verified.verificationToken);
        setStep('details');
      } catch (caught) {
        setError(authErrorMessage(caught, t));
      } finally {
        setBusy(false);
      }
    })();
  };

  const resend = (): void => {
    setResending(true);
    void (async () => {
      try {
        setChallenge(await startSignup(client, email));
        setError(null);
      } catch (caught) {
        setError(authErrorMessage(caught, t));
      } finally {
        setResending(false);
      }
    })();
  };

  const submitDetails = (): void => {
    const parsedPassword = passwordSchema.safeParse(password);
    if (!parsedPassword.success) {
      setPasswordError(parsedPassword.error.issues[0]?.message);
      return;
    }
    setPasswordError(undefined);

    setBusy(true);
    setError(null);
    void (async () => {
      try {
        const session = await completeSignup(client, {
          verificationToken,
          ownerName,
          shopName,
          password,
        });
        rememberDevice({ email, method: 'password' });
        auth.signIn({
          user: session.user,
          shop: session.shop,
          role: session.role,
          sessionId: session.sessionId,
          // A brand-new shop is the only one this owner has.
          memberships: session.shop
            ? [{ tenantId: session.shop.id, name: session.shop.name, role: session.role }]
            : [],
        });
        // No navigation here: `GuestOnly` sees the new session and moves them into the app.
      } catch (caught) {
        setError(authErrorMessage(caught, t));
        // A verification token is single-use and expires; if the server has rejected it there
        // is nothing to do on this screen but start again with the address.
        setBusy(false);
      }
    })();
  };

  const footer = (
    <span>
      {t('auth.haveAccount')}{' '}
      <Link to="/login" className="font-medium text-primary underline-offset-4 hover:underline">
        {t('auth.signIn')}
      </Link>
    </span>
  );

  if (step === 'code' && challenge) {
    return (
      <AuthLayout
        title={t('auth.verifyEmailTitle')}
        description={t('auth.verifyEmailDescription')}
        step={{ current: 2, total: 3 }}
        footer={footer}
      >
        <OtpStep
          challenge={challenge}
          submitting={busy}
          resending={resending}
          error={error}
          onSubmit={submitCode}
          onResend={resend}
          onChangeNumber={() => {
            setStep('email');
            setError(null);
          }}
        />
      </AuthLayout>
    );
  }

  if (step === 'details') {
    return (
      <AuthLayout
        title={t('auth.shopDetailsTitle')}
        description={t('auth.shopDetailsDescription')}
        step={{ current: 3, total: 3 }}
        footer={footer}
      >
        <form
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            submitDetails();
          }}
          noValidate
        >
          <FormError message={error} />

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="owner-name">{t('auth.yourName')}</Label>
            <Input
              id="owner-name"
              autoComplete="name"
              value={ownerName}
              onChange={(event) => setOwnerName(event.target.value)}
              placeholder={t('auth.yourNamePlaceholder')}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="shop-name">{t('auth.shopName')}</Label>
            <Input
              id="shop-name"
              autoComplete="organization"
              value={shopName}
              onChange={(event) => setShopName(event.target.value)}
              placeholder={t('auth.shopNamePlaceholder')}
            />
          </div>

          <PasswordField
            label={t('auth.choosePassword')}
            hint={t('auth.passwordHint')}
            error={passwordError}
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />

          <Button type="submit" block disabled={busy}>
            {t('auth.createShop')}
          </Button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth.signupTitle')}
      description={t('auth.signupDescription')}
      step={{ current: 1, total: 3 }}
      footer={footer}
    >
      <form
        className="flex flex-col gap-5"
        onSubmit={(event) => {
          event.preventDefault();
          submitEmail();
        }}
        noValidate
      >
        <FormError message={error} />

        <EmailField
          label={t('auth.emailAddress')}
          hint={t('auth.emailAddressHint')}
          error={emailError}
          value={email}
          autoFocus
          onChange={(event) => setEmail(event.target.value)}
        />

        <Button type="submit" block disabled={busy}>
          {t('auth.sendCode')}
        </Button>

        {/* The objection that closes the page, answered before anything is typed. */}
        <p className="rounded-md border border-success/30 bg-success/10 px-3 py-2 text-sm text-success">
          {t('auth.trialPromise')}
        </p>
      </form>
    </AuthLayout>
  );
}

export default SignupPage;
